import { Data } from "./data.model";
import { DataAggregation } from "./dataAggregation.model";
import { DataTypeModelKey, IDataType } from "./dataType.model";
import { DeviceModel } from "./device.model";
import { EventModelSeverity } from "./event.model";
import { AlarmModelTimeWindow } from "./alarm.model";

export class Location {
    address: string;
    latitude: number;
    longitude: number;
}

export class ProjectDeviceEvent {
    severity: EventModelSeverity;
    window: AlarmModelTimeWindow;
    eventId: string;
    timestamp: Date;
}

export class AverageData implements IDataType<DataAggregation<any>> {
    soundPressureLevel?: DataAggregation<number>;
    soundPressureLevelC?: DataAggregation<number>;
    soundPressureLevel1Minute?: DataAggregation<number>;
    soundPressureLevel1MinuteSensor2?: DataAggregation<number>;
    soundPressureLevelC1Minute?: DataAggregation<number>;
    soundPressureLevelCMinusA?: DataAggregation<number>;
    rolling5s?: DataAggregation<number>;
    rolling5sC?: DataAggregation<number>;
    rolling5?: DataAggregation<number>;
    rolling5C?: DataAggregation<number>;
    rolling30?: DataAggregation<number>;
    rolling30C?: DataAggregation<number>;
    battery?: DataAggregation<number>;
    overload?: DataAggregation<boolean>;
    underrange?: DataAggregation<boolean>;
    powerSupplyStatus?: DataAggregation<boolean>;
    wifiSignalLevel?: DataAggregation<number>;
    cellullarSignalLevel?: DataAggregation<number>;
    temperature?: DataAggregation<number>;
    spectrum13Octave?: DataAggregation<number[]>;
    spectrum13Octave1Minute?: DataAggregation<number[]>;
    freeStorage?: DataAggregation<number>;
}

export class LastData implements IDataType<Data<any>> {
    soundPressureLevel?: Data<number>;
    soundPressureLevelC?: Data<number>;
    soundPressureLevel1Minute?: Data<number>;
    soundPressureLevel1MinuteSensor2?: Data<number>;
    soundPressureLevelC1Minute?: Data<number>;
    soundPressureLevelCMinusA?: Data<number>;
    rolling5s?: Data<number>;
    rolling5sC?: Data<number>;
    rolling5?: Data<number>;
    rolling5C?: Data<number>;
    rolling30?: Data<number>;
    rolling30C?: Data<number>;
    battery?: Data<number>;
    overload?: Data<boolean>;
    underrange?: Data<boolean>;
    powerSupplyStatus: Data<boolean>;
    wifiSignalLevel: Data<number>;
    cellullarSignalLevel: Data<number>;
    temperature?: Data<number>;
    spectrum13Octave?: Data<number[]>;
    spectrum13Octave1Minute?: Data<number[]>;
    freeStorage?: Data<number>;
}

export enum DeviceOperationType {
    Reset = "reset",
}

export enum DeviceOperationStatus {
    Pending = "pending",
    InProgress = "in_progress",
    Completed = "completed",
    Failed = "failed",
}

export class DeviceOperation {
    type: DeviceOperationType;
    status: DeviceOperationStatus;
    requestedAt: Date;
    startedAt?: Date;
    finishedAt?: Date;
    error?: string;
}

export enum ScheduledRestartFrequency {
    Weekly = "weekly",
    Monthly = "monthly",
}

export class ScheduledRestart {
    enabled: boolean;
    frequency?: ScheduledRestartFrequency;
    weekday?: number;
    monthDay?: number;
    hour: number;
    minute: number;
    timezone?: string;
    lastTriggeredAt?: Date;
}

export enum ProjectDeviceConnectionType {
    LF010 = "lf010",
    XL3 = "xl3",
}

export enum XL3ConnectionMode {
    Lan = "lan",
    Connect = "connect",
}

export class ProjectDeviceConnectionSettings {
    type?: ProjectDeviceConnectionType;
    mode?: XL3ConnectionMode;
    host?: string;
    password?: string;
    connectKey?: string;
}

export class LF010DevicePeriod {
    devicePeriod: number;
    name: string;
    emissionLimit?: number;
}

export class LF010LimitHistoryEntry {
    devicePeriod: number;
    name: string;
    previousEmissionLimit: number;
    emissionLimit: number;
    changedAt: Date;
}

export class LF010PeriodMapping {
    devicePeriod: number;
    projectPeriodName: string;
}

export class ProjectDeviceLF010Settings {
    devicePeriods?: LF010DevicePeriod[];
    limitHistory?: LF010LimitHistoryEntry[];
    periodMappings?: LF010PeriodMapping[];
    managedAlarmIds?: string[];
}

export class ProjectDeviceRuntimeSettings {
    customConfig?: string;
    fileSyncEnabled?: boolean;
    useServerClock?: boolean;
    sentiloData?: string;
}

export class ProjectDeviceSchedulingSettings {
    restart?: ScheduledRestart;
}

export class ProjectDeviceSpecialAlarmSettings {
    connectionEnabled?: boolean;
    powerSupplyEnabled?: boolean;
    notificationChannelIds?: string[];
}

export class ProjectDeviceSettings {
    connection?: ProjectDeviceConnectionSettings;
    lf010?: ProjectDeviceLF010Settings;
    runtime?: ProjectDeviceRuntimeSettings;
    scheduling?: ProjectDeviceSchedulingSettings;
    specialAlarms?: ProjectDeviceSpecialAlarmSettings;
}

export class ProjectDeviceModel {
    id: string;
    name?: string;
    baseDevice: string;
    baseDeviceData: DeviceModel;
    manufacturer: string;
    project: string;
    readonly picture?: string;
    comments: string;
    ftpFolder: string;
    settings?: ProjectDeviceSettings;
    active: boolean;
    dataFrequency: number;
    storage?: number;
    location: Location;
    lastData: LastData;
    dataAggregation: AverageData;
    fields: DataTypeModelKey[];
    connected: boolean;
    activeEvents: ProjectDeviceEvent[];
    operation?: DeviceOperation;

    /**
     * Fallback for retro compatibility: some old devices doese not have a device name
     * and shall use the name of the parent device.
     */
    public get deviceName() {
        if (this.name) {
            return this.name;
        }

        return this.baseDeviceData ? this.baseDeviceData.name : "";
    }

    /**
     * Extract the last data update time by cycling through it
     */
    static lastDataTime(lastData: LastData): Date {
        for (const key in lastData) {
            if (Object.prototype.hasOwnProperty.call(lastData, key) && lastData[key]) {
                const data = lastData[key] as Data<any>;
                if (data.timestamp !== undefined) {
                    return data.timestamp;
                }
            }
        }

        return null;
    }

    getLastDataValueAsString(dataType: DataTypeModelKey): string {
        switch (dataType) {
            case DataTypeModelKey.soundPressureLevel:
                return this.lastData && this.lastData.soundPressureLevel ? this.lastData.soundPressureLevel.value.toFixed(1) + " dBA" : "-";
            case DataTypeModelKey.soundPressureLevelC:
                return this.lastData && this.lastData.soundPressureLevelC
                    ? this.lastData.soundPressureLevelC.value.toFixed(1) + " dBC"
                    : "-";
            case DataTypeModelKey.soundPressureLevel1Minute:
                return this.lastData && this.lastData.soundPressureLevel1Minute
                    ? this.lastData.soundPressureLevel1Minute.value.toFixed(1) + " dBA"
                    : "-";
            case DataTypeModelKey.soundPressureLevel1MinuteSensor2:
                return this.lastData && this.lastData.soundPressureLevel1MinuteSensor2
                    ? this.lastData.soundPressureLevel1MinuteSensor2.value.toFixed(1) + " dBA"
                    : "-";
            case DataTypeModelKey.soundPressureLevelC1Minute:
                return this.lastData && this.lastData.soundPressureLevelC1Minute
                    ? this.lastData.soundPressureLevelC1Minute.value.toFixed(1) + " dBC"
                    : "-";
            case DataTypeModelKey.soundPressureLevelCMinusA:
                return this.lastData && this.lastData.soundPressureLevelCMinusA
                    ? this.lastData.soundPressureLevelCMinusA.value.toFixed(1) + " dB"
                    : "-";
            case DataTypeModelKey.rolling5s:
                return this.lastData && this.lastData.rolling5s ? this.lastData.rolling5s.value.toFixed(1) + " dBA" : "-";
            case DataTypeModelKey.rolling5sC:
                return this.lastData && this.lastData.rolling5sC ? this.lastData.rolling5sC.value.toFixed(1) + " dBC" : "-";
            case DataTypeModelKey.rolling5:
                return this.lastData && this.lastData.rolling5 ? this.lastData.rolling5.value.toFixed(1) + " dBA" : "-";
            case DataTypeModelKey.rolling5C:
                return this.lastData && this.lastData.rolling5C ? this.lastData.rolling5C.value.toFixed(1) + " dBC" : "-";
            case DataTypeModelKey.rolling30:
                return this.lastData && this.lastData.rolling30 ? this.lastData.rolling30.value.toFixed(1) + " dBA" : "-";
            case DataTypeModelKey.rolling30C:
                return this.lastData && this.lastData.rolling30C ? this.lastData.rolling30C.value.toFixed(1) + " dBC" : "-";
        }
    }

    getLastDataDate(dataType: DataTypeModelKey): Date {
        switch (dataType) {
            case DataTypeModelKey.soundPressureLevel:
                return this.lastData && this.lastData.soundPressureLevel ? this.lastData.soundPressureLevel.timestamp : null;
            case DataTypeModelKey.soundPressureLevelC:
                return this.lastData && this.lastData.soundPressureLevelC ? this.lastData.soundPressureLevelC.timestamp : null;
            case DataTypeModelKey.soundPressureLevel1Minute:
                return this.lastData && this.lastData.soundPressureLevel1Minute ? this.lastData.soundPressureLevel1Minute.timestamp : null;
            case DataTypeModelKey.soundPressureLevel1MinuteSensor2:
                return this.lastData && this.lastData.soundPressureLevel1MinuteSensor2
                    ? this.lastData.soundPressureLevel1MinuteSensor2.timestamp
                    : null;
            case DataTypeModelKey.soundPressureLevelC1Minute:
                return this.lastData && this.lastData.soundPressureLevelC1Minute
                    ? this.lastData.soundPressureLevelC1Minute.timestamp
                    : null;
            case DataTypeModelKey.soundPressureLevelCMinusA:
                return this.lastData && this.lastData.soundPressureLevelCMinusA ? this.lastData.soundPressureLevelCMinusA.timestamp : null;
            case DataTypeModelKey.rolling5s:
                return this.lastData && this.lastData.rolling5s ? this.lastData.rolling5s.timestamp : null;
            case DataTypeModelKey.rolling5sC:
                return this.lastData && this.lastData.rolling5sC ? this.lastData.rolling5sC.timestamp : null;
            case DataTypeModelKey.rolling5:
                return this.lastData && this.lastData.rolling5 ? this.lastData.rolling5.timestamp : null;
            case DataTypeModelKey.rolling5C:
                return this.lastData && this.lastData.rolling5C ? this.lastData.rolling5C.timestamp : null;
            case DataTypeModelKey.rolling30:
                return this.lastData && this.lastData.rolling30 ? this.lastData.rolling30.timestamp : null;
            case DataTypeModelKey.rolling30C:
                return this.lastData && this.lastData.rolling30C ? this.lastData.rolling30C.timestamp : null;
        }
    }

    getAlarmStatus(dataType: DataTypeModelKey, window?: AlarmModelTimeWindow): EventModelSeverity {
        let status: EventModelSeverity = EventModelSeverity.EventModelSeveritySuccess;

        if (this.activeEvents && this.activeEvents[dataType]) {
            this.activeEvents[dataType].forEach((element) => {
                if (element.window == window || window == null) {
                    if (status != EventModelSeverity.EventModelSeverityAlarm) {
                        status = element.severity;
                    }
                }
            });
        }

        return status;
    }
}
